import * as React from "react";

import {analyticCount, analyticSum} from "../analytics-locale";
import {xdQAattribute}              from "../../analytics-layout";
import "./american-pie-chart.scss";

const SIDE_VIEWBOX = 200
const KEY_ANALYTIC = 'orderNames'
const RADIUS_FACTOR_INNER = 0.6
const RADIUS_FACTOR_OUTER = 0.9
const DISPLAY_MAX = 5

export const AmericanPieChart = ({leadInteractions, colorsFill, filters}) => {
    const viewBox = `0 0 ${SIDE_VIEWBOX} ${SIDE_VIEWBOX}`
    const [hoveredValue, setHoveredValue] = React.useState(null)
    const values = analyticCount(leadInteractions, KEY_ANALYTIC)
    const sum = analyticSum(values, DISPLAY_MAX)
    const leadCount = displayedLeadsCount(leadInteractions, sum, filters)
    const wedgeData = createWedgeData(values, sum, filters)

    const colorLeadCount = "#334150"
    const colorLeadText = "#33415080"
    const colorBackground = "#33415040"
    const fontSizeLeadCount = 24
    const fontSizeLeadText = 8.5
    const leadText = leadCount === 1 ? "Lead" : "Leads"
    const leadTextOffsetX = leadCount === 1 ? -7.5 : -10.0

    return (
        <div className="american-pie-11235">
            <div className="pie-header">
                <h5 
                    className="title"
                    {...xdQAattribute('label', 'order title')}
                >
                    Orders
                </h5>
                <p 
                    className="subtitle"
                    {...xdQAattribute('text', 'order subtitle')}
                >
                    {subheaderText(values)}
                </p>
            </div>
            <div className="pie-data">
                <svg
                    className="american-pie"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox={viewBox}
                    {...xdQAattribute('image', 'order chart')}
                >   
                    {
                        (sum < 1) &&
                        <path 
                            className='pie-wedge-background'
                            d={
                                createWedgePath(
                                    Math.floor(RADIUS_FACTOR_INNER * SIDE_VIEWBOX / 2), 
                                    Math.floor(RADIUS_FACTOR_OUTER * SIDE_VIEWBOX / 2), 
                                    0.0001,
                                    2 * Math.PI
                                )
                            }
                            fill={colorBackground}
                            stroke="none"
                            {...xdQAattribute('image', 'order wedge background')}
                        />
                    }
                    {
                        wedgeData.map((wedge, i) => {
                            const stroke = wedgeData.length == 1 ? colorsFill[i % colorsFill.length] : 'none'
                            return (
                                <Wedge 
                                    key={i}
                                    wedge={wedge}
                                    fillColor={colorsFill[i % colorsFill.length]}
                                    stroke={stroke}
                                    setHoveredValue={setHoveredValue}
                                />
                            )
                        })
                    }
                    { 
                        leadInteractions?.length > 0 &&
                        <>
                            <text 
                                x={orderCountOffsetX(leadCount)} 
                                y={SIDE_VIEWBOX / 2}
                                fill={colorLeadCount}
                                fontSize={fontSizeLeadCount}
                                fontWeight={'bold'}
                                {...xdQAattribute('text', `lead count`)}
                            >
                                {leadCount.toLocaleString()}
                            </text>
                            <text 
                                x={SIDE_VIEWBOX / 2 + leadTextOffsetX} 
                                y={SIDE_VIEWBOX / 2 + fontSizeLeadCount}
                                fill={colorLeadText}
                                fontSize={fontSizeLeadText}
                                fontWeight={'normal'}
                                {...xdQAattribute('label',  leadText)}
                            >
                                {leadText}
                            </text>
                        </>
                    }
                </svg>
                <div className="pie-legend">
                    {
                        values.map((value, i) => {
                            const orderName = value[KEY_ANALYTIC]
                            const orderCount = value.count

                            return (
                                (i < DISPLAY_MAX && orderNameSurvivesFilter(filters, value)) &&
                                <div
                                    key={i}
                                    className={`order-list-item-container 
                                        ${orderName === hoveredValue ? ' hovered' : ''}`}
                                >
                                    <li
                                        className='order-list-item'
                                        style={{color: colorsFill[i % colorsFill.length]}}
                                    >
                                        <span className="order-text">
                                            <p
                                                className="order-name"
                                                {...xdQAattribute('text', orderName)}
                                            >
                                                {orderName}
                                            </p>
                                            <p
                                                className="order-count"
                                                {...xdQAattribute('text', `count ${orderName}`)}
                                            >
                                                {orderCount}
                                            </p>
                                        </span>
                                    </li>
                                </div>
                            )
                        })
                    }
                </div>
            </div>
        </div>
    )
}

const orderNameSurvivesFilter = (filters, value) => {
    const orderName = value[KEY_ANALYTIC]
    const orderNameFilter = filters.find(filter => filter.filterKey === "orderName")
    if (!orderNameFilter) {
        return true;
    }
    
    return orderNameFilter.values.includes(orderName)
}

const orderCountOffsetX = (sum) => {
    const centerX = SIDE_VIEWBOX / 2
    const offsetComma = 0.35

    let offsetCharacter = 7
    if(sum > 999) {
        offsetCharacter += offsetComma
    }
    if(sum > 999999) {
        offsetCharacter += offsetComma
    }

    const orderOfMagnitude = sum.toString().length
    
    return centerX - offsetCharacter * orderOfMagnitude
}

const subheaderText = (values) => {
    if(!values) {
        return 'No leads found'
    }
    if(values.length === 0) {
        return 'No leads found'
    }
    if(values.length === 1) {
        return 'Order with the most leads'
    }

    return 'Orders with the most leads'
}

const Wedge = ({wedge, stroke, fillColor, setHoveredValue}) => {
    const [isHovered, setIsHovered] = React.useState(false)
    const ref = React.useRef(null)

    const twoDegrees = 0.034
    const fourDegrees = 0.068
    const marginOuter = 2
    const angle = wedge.angleEnd - wedge.angleStart
    const shouldReduceAngle = angle < (2 * Math.PI - 0.1) && fourDegrees < angle 
    const angleReduction = shouldReduceAngle ? twoDegrees : 0
    const radiusInnerBase = Math.floor(RADIUS_FACTOR_INNER * SIDE_VIEWBOX / 2) 
    const radiusOuterBase = Math.floor(RADIUS_FACTOR_OUTER * SIDE_VIEWBOX / 2)
    const radiusShift = SIDE_VIEWBOX / 2 - radiusOuterBase - marginOuter
    const translucent = "#FFFFFF00"

    const handlePointerEnter = () => {
        setHoveredValue(wedge[KEY_ANALYTIC])
        setIsHovered(true)
    }

    const handlePointerLeave = () => {
        setHoveredValue(null)
        setIsHovered(false)
    }

    const radiusInner = isHovered ? radiusInnerBase + radiusShift : radiusInnerBase
    const radiusOuter = isHovered ? radiusOuterBase + radiusShift : radiusOuterBase
    const angleStart = isHovered ? wedge.angleStart + angleReduction : wedge.angleStart
    const angleEnd = isHovered ? wedge.angleEnd - angleReduction : wedge.angleEnd
    const radiusInnerTranslucent = radiusInnerBase
    const radiusOuterTranslucent = radiusOuterBase + radiusShift
    
    return (
        <>
            <path 
                className={`pie-wedge ${isHovered ? 'hovered' : ''}`}
                d={createWedgePath(radiusInner, radiusOuter, angleStart, angleEnd)}
                fill={fillColor}
                stroke={stroke}
                style={{
                    transition: 'all 0.4s ease-in-out 0.2s'
                }}
                {...xdQAattribute('image', 'pie wedge')}
            />
            <path 
                className="pie-wedge"
                onPointerEnter={handlePointerEnter}
                onPointerLeave={handlePointerLeave}
                d={createWedgePath(radiusInnerTranslucent, radiusOuterTranslucent, angleStart, angleEnd)}
                ref={ref}
                fill={translucent}
                stroke="none"
            />
        </>
    )
}

const createWedgePath = (radiusInner, radiusOuter, angleStart, angleEnd) => {
    const centerX = Math.floor(SIDE_VIEWBOX / 2)
    const centerY =  Math.floor(SIDE_VIEWBOX / 2)
    const largeArcFlag = angleEnd - angleStart > Math.PI ? 1 : 0
    
    const move = `M ${centerX + Math.cos(angleStart) * radiusInner},${centerY + Math.sin(angleStart) * radiusInner} `
    const lineOut = `L ${centerX + Math.cos(angleStart) * radiusOuter},${centerY + Math.sin(angleStart) * radiusOuter} `
    const arcOuter = `A ${radiusOuter},${radiusOuter} 0 ${largeArcFlag},1 ${centerX + Math.cos(angleEnd) * radiusOuter},${centerY + Math.sin(angleEnd) * radiusOuter} `
    const lineIn = `L ${centerX + Math.cos(angleEnd) * radiusInner},${centerY + Math.sin(angleEnd) * radiusInner}`
    const arcInner = `A ${radiusInner},${radiusInner} 0 ${largeArcFlag},0 ${centerX + Math.cos(angleStart) * radiusInner},${centerY + Math.sin(angleStart) * radiusInner} Z`

    return (move + lineOut + arcOuter + lineIn + arcInner)
}

const createWedgeData = (values, sum, filters) => {
    let angleStart = 0
    const wedgeData = []

    let divisor = sum

    const orderNameFilter = filters.find(filter => filter.filterKey === "orderName")
    if(orderNameFilter) {
        let count = 0
        values.forEach(value => {
            if(orderNameSurvivesFilter(filters, value)) {
                count += value.count
            }
        })
        divisor = count
    }

    const limit = calculateLimit(values, filters)
    for(let i = 0; i < limit; i++) {
        const angleEnd = angleStart + (2 * Math.PI) * values[i].count / divisor
        wedgeData.push({[KEY_ANALYTIC]: values[i][KEY_ANALYTIC], angleStart: angleStart, angleEnd: angleEnd.toFixed(3)})
        angleStart = angleEnd
    }
    
    return wedgeData
}

const calculateLimit = (values, filters) => {
    if(values.length > DISPLAY_MAX) {
        return DISPLAY_MAX
    }
    let limit = 0

    for(let i = 0; i < values.length; i++) {
        if(limit > DISPLAY_MAX) {
            return DISPLAY_MAX
        }
        if(orderNameSurvivesFilter(filters, values[i])) {
            limit++
        }
    }

    return limit
}

const displayedLeadsCount = (leadInteractions, sum, filters) => {
    const orderNameFilter = filters.find(filter => filter.filterKey === "orderName")
    if(!orderNameFilter) {
        return Math.min(leadInteractions?.length, sum)
    }

    const filterValuesSet = new Set(orderNameFilter.values)
    let count = 0
    for(let i = 0; i < leadInteractions?.length; i++) {
        const orderNames = new Set(leadInteractions[i][KEY_ANALYTIC])
        if(orderNames.isDisjointFrom(filterValuesSet)) {
            continue
        }
        count++
    }
    return count
}